import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import Navbar from "../../../components/navbar";
import Usertab from "../../../components/user-tab";
import Footer from "../../../components/footer";
import Switcher from "../../../components/switcher";

import {
  FiUser,
  FiUserCheck,
  FiMail,
  FiPhone,
  FiKey,
  FiEye,
  FiEyeOff,
} from "../../../assets/icons/vander";
import ScrollToTop from "../../../components/scroll-to-top";

export default function UserSetting() {
  const [profile, setProfile] = useState({
    first_name: "",
    last_name: "",
    email: "",
    gender: "",
    mobile_no: "",
    whatsapp_no: "",
  });
  const [loading, setLoading] = useState(true);
  const [passwords, setPasswords] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({
    old_password: false,
    new_password: false,
    confirm_password: false,
    password_match: false,
    password_strength: false,
  });
  const [showPasswords, setShowPasswords] = useState({
    old_password: false,
    new_password: false,
    confirm_password: false,
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const customerId = localStorage.getItem("customer_id");
        if (!customerId) {
          console.error("No customer ID found in localStorage");
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/profile/?customer_id=${customerId}`
        );
        const customerDetails = response.data.customer_details;

        setProfile({
          first_name: customerDetails.first_name || "",
          last_name: customerDetails.last_name || "",
          email: customerDetails.email || "",
          gender: customerDetails.gender || "",
          mobile_no: customerDetails.mobile_no || "",
          whatsapp_no: customerDetails.whatsapp_no || "",
        });

        localStorage.setItem("customer_first_name", profile.first_name);
        localStorage.setItem("customer_last_name", profile.last_name);
        localStorage.setItem("customer_email", profile.email);
      } catch (error) {
        console.error("Failed to fetch profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const validatePasswordStrength = (password) => {
    // Password must be at least 8 characters with letters and digits
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasMinLength = password.length >= 8;
    
    return hasLetter && hasDigit && hasMinLength;
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords((prev) => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: false }));
    }
    
    // Check password match when confirm password changes
    if (name === "confirm_password" || name === "new_password") {
      const newPassword = name === "new_password" ? value : passwords.new_password;
      const confirmPassword = name === "confirm_password" ? value : passwords.confirm_password;
      
      if (confirmPassword && newPassword !== confirmPassword) {
        setPasswordErrors(prev => ({ ...prev, password_match: true }));
      } else {
        setPasswordErrors(prev => ({ ...prev, password_match: false }));
      }
    }
    
    // Validate password strength when new password changes
    if (name === "new_password") {
      if (value && !validatePasswordStrength(value)) {
        setPasswordErrors(prev => ({ ...prev, password_strength: true }));
      } else {
        setPasswordErrors(prev => ({ ...prev, password_strength: false }));
      }
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validatePasswords = () => {
    let isValid = true;
    const errors = {
      old_password: !passwords.old_password,
      new_password: !passwords.new_password,
      confirm_password: !passwords.confirm_password,
      password_match: passwords.new_password !== passwords.confirm_password,
      password_strength: passwords.new_password && !validatePasswordStrength(passwords.new_password),
    };

    setPasswordErrors(errors);

    // Check if any error is true
    Object.values(errors).forEach(error => {
      if (error) isValid = false;
    });

    return isValid;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const customerId = localStorage.getItem("customer_id");
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/profile/?customer_id=${customerId}`,
        {
          ...profile,
        }
      );
      toast.success("Profile updated successfully!", {
        position: "top-center",
        style: {
          borderRadius: "10px",
          background: "#333",
          color: "#fff",
        },
      });

      localStorage.setItem("customer_first_name", profile.first_name);
      localStorage.setItem("customer_last_name", profile.last_name);
      localStorage.setItem("customer_email", profile.email);
    } catch (error) {
      console.error("Failed to update profile:", error);
      toast.error("Failed to update profile. Please try again.", {
        position: "top-center",
        style: {
          borderRadius: "10px",
          background: "#333",
          color: "#fff",
        },
      });
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswords()) {
      toast.error("Please fix the errors in the form", {
        position: "top-center",
        style: {
          borderRadius: "10px",
          background: "#333",
          color: "#fff",
        },
      });
      return;
    }
    
    try {
      const accessToken = localStorage.getItem("access_token");
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/change-password/`,
        {
          old_password: passwords.old_password,
          new_password: passwords.new_password,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`, // Include the token in the header
          },
        }
      );
      toast.success("Password updated successfully!", {
        position: "top-center",
        style: {
          borderRadius: "10px",
          background: "#333",
          color: "#fff",
        },
      });
      setPasswords({
        old_password: "",
        new_password: "",
        confirm_password: "",
      });
      setPasswordErrors({
        old_password: false,
        new_password: false,
        confirm_password: false,
        password_match: false,
        password_strength: false,
      });
    } catch (error) {
      console.error("Failed to update password:", error);
      toast.error("Failed to update password. Please try again.", {
        position: "top-center",
        style: {
          borderRadius: "10px",
          background: "#333",
          color: "#fff",
        },
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl font-medium">Loading profile...</div>
      </div>
    );
  }

  return (
    <>
      <Navbar navClass="defaultscroll is-sticky" />

      <section className="relative lg:pb-24 pb-16 md:mt-[84px] mt-[70px]">
        <div className="md:container container-fluid relative">
          <div className="relative overflow-hidden md:rounded-md shadow dark:shadow-gray-700 h-52 bg-[url('../../assets/images/hero/pages.jpg')] bg-center bg-no-repeat bg-cover"></div>
        </div>

        <div className="container relative md:mt-24 mt-16">
          <div className="md:flex">
            <Usertab />

            <div className="lg:w-3/4 md:w-2/3 md:px-3 mt-6 md:mt-0">
              {/* Personal Details Form - Now includes Contact Info */}
              <div className="p-6 rounded-md shadow dark:shadow-gray-800 bg-white dark:bg-slate-900">
                <h5 className="text-lg font-semibold mb-4">
                  Personal Details :
                </h5>
                <form onSubmit={handleProfileSubmit}>
                  <div className="grid lg:grid-cols-2 grid-cols-1 gap-5">
                    <div>
                      <label className="form-label font-medium">
                        First Name : <span className="text-red-600">*</span>
                      </label>
                      <div className="form-icon relative mt-2">
                        <FiUser className="w-4 h-4 absolute top-3 start-4"></FiUser>
                        <input
                          type="text"
                          className="ps-12 w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 dark:border-gray-800 focus:ring-0"
                          placeholder="First Name"
                          id="firstname"
                          name="first_name"
                          value={profile.first_name}
                          onChange={handleProfileChange}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="form-label font-medium">
                        Last Name : <span className="text-red-600">*</span>
                      </label>
                      <div className="form-icon relative mt-2">
                        <FiUserCheck className="w-4 h-4 absolute top-3 start-4"></FiUserCheck>
                        <input
                          type="text"
                          className="ps-12 w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 dark:border-gray-800 focus:ring-0"
                          placeholder="Last Name"
                          id="lastname"
                          name="last_name"
                          value={profile.last_name}
                          onChange={handleProfileChange}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="form-label font-medium">
                        Your Email : <span className="text-red-600">*</span>
                      </label>
                      <div className="form-icon relative mt-2">
                        <FiMail className="w-4 h-4 absolute top-3 start-4"></FiMail>
                        <input
                          type="email"
                          className="ps-12 w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 dark:border-gray-800 focus:ring-0"
                          placeholder="Email"
                          name="email"
                          value={profile.email}
                          onChange={handleProfileChange}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="form-label font-medium">Gender :</label>
                      <div className="form-icon relative mt-2">
                        <select
                          name="gender"
                          value={profile.gender}
                          onChange={handleProfileChange}
                          className="ps-12 w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 dark:border-gray-800 focus:ring-0"
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                          <option value="prefer_not_to_say">
                            Prefer not to say
                          </option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="form-label font-medium">
                        Mobile Number :
                      </label>
                      <div className="form-icon relative mt-2">
                        <FiPhone className="w-4 h-4 absolute top-3 start-4"></FiPhone>
                        <input
                          name="mobile_no"
                          id="mobile_no"
                          type="tel"
                          className="ps-12 w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 dark:border-gray-800 focus:ring-0"
                          placeholder="Mobile Number"
                          value={profile.mobile_no}
                          onChange={handleProfileChange}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="form-label font-medium">
                        WhatsApp Number :
                      </label>
                      <div className="form-icon relative mt-2">
                        <FiPhone className="w-4 h-4 absolute top-3 start-4"></FiPhone>
                        <input
                          name="whatsapp_no"
                          id="whatsapp_no"
                          type="tel"
                          className="ps-12 w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border border-gray-100 dark:border-gray-800 focus:ring-0"
                          placeholder="WhatsApp Number"
                          value={profile.whatsapp_no}
                          onChange={handleProfileChange}
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="py-2 px-5 inline-block font-semibold tracking-wide align-middle duration-500 text-base text-center bg-green-500 text-white rounded-md mt-5"
                  >
                    Save Changes
                  </button>
                </form>
              </div>

              {/* Password Change Form */}
              <div className="p-6 rounded-md shadow dark:shadow-gray-800 bg-white dark:bg-slate-900 mt-6">
                <h5 className="text-lg font-semibold mb-4">
                  Change Password :
                </h5>
                <form onSubmit={handlePasswordSubmit}>
                  <div className="grid grid-cols-1 gap-5">
                    <div>
                      <label className="form-label font-medium">
                        Old Password :
                      </label>
                      <div className="form-icon relative mt-2">
                        <FiKey className="w-4 h-4 absolute top-3 start-4"></FiKey>
                        <input
                          type={showPasswords.old_password ? "text" : "password"}
                          name="old_password"
                          value={passwords.old_password}
                          onChange={handlePasswordChange}
                          className={`ps-12 pe-12 w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border focus:ring-0 ${
                            passwordErrors.old_password
                              ? "border-red-500"
                              : "border-gray-100 dark:border-gray-800"
                          }`}
                          placeholder="Old Password"
                          required
                        />
                        <button
                          type="button"
                          className="absolute top-3 end-4"
                          onClick={() => togglePasswordVisibility("old_password")}
                        >
                          {showPasswords.old_password ? (
                            <FiEyeOff className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          ) : (
                            <FiEye className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          )}
                        </button>
                      </div>
                      {passwordErrors.old_password && (
                        <p className="text-red-500 text-sm mt-1">
                          Old password is required
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="form-label font-medium">
                        New Password :
                      </label>
                      <div className="form-icon relative mt-2">
                        <FiKey className="w-4 h-4 absolute top-3 start-4"></FiKey>
                        <input
                          type={showPasswords.new_password ? "text" : "password"}
                          name="new_password"
                          value={passwords.new_password}
                          onChange={handlePasswordChange}
                          className={`ps-12 pe-12 w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border focus:ring-0 ${
                            passwordErrors.new_password || passwordErrors.password_strength
                              ? "border-red-500"
                              : "border-gray-100 dark:border-gray-800"
                          }`}
                          placeholder="New Password"
                          required
                        />
                        <button
                          type="button"
                          className="absolute top-3 end-4"
                          onClick={() => togglePasswordVisibility("new_password")}
                        >
                          {showPasswords.new_password ? (
                            <FiEyeOff className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          ) : (
                            <FiEye className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          )}
                        </button>
                      </div>
                      {passwordErrors.new_password && (
                        <p className="text-red-500 text-sm mt-1">
                          New password is required
                        </p>
                      )}
                      {passwordErrors.password_strength && (
                        <p className="text-red-500 text-sm mt-1">
                          Password must be at least 8 characters with letters and digits
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="form-label font-medium">
                        Confirm New Password :
                      </label>
                      <div className="form-icon relative mt-2">
                        <FiKey className="w-4 h-4 absolute top-3 start-4"></FiKey>
                        <input
                          type={showPasswords.confirm_password ? "text" : "password"}
                          name="confirm_password"
                          value={passwords.confirm_password}
                          onChange={handlePasswordChange}
                          className={`ps-12 pe-12 w-full py-2 px-3 h-10 bg-transparent dark:bg-slate-900 dark:text-slate-200 rounded outline-none border focus:ring-0 ${
                            passwordErrors.confirm_password || passwordErrors.password_match
                              ? "border-red-500"
                              : "border-gray-100 dark:border-gray-800"
                          }`}
                          placeholder="Confirm New Password"
                          required
                        />
                        <button
                          type="button"
                          className="absolute top-3 end-4"
                          onClick={() => togglePasswordVisibility("confirm_password")}
                        >
                          {showPasswords.confirm_password ? (
                            <FiEyeOff className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          ) : (
                            <FiEye className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          )}
                        </button>
                      </div>
                      {passwordErrors.confirm_password && (
                        <p className="text-red-500 text-sm mt-1">
                          Please confirm your new password
                        </p>
                      )}
                      {passwordErrors.password_match && (
                        <p className="text-red-500 text-sm mt-1">
                          Passwords do not match
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      <span className="font-medium">Password requirements:</span> At least 8 characters with letters and digits
                    </p>
                  </div>

                  <button
                    type="submit"
                    className="py-2 px-5 inline-block font-semibold tracking-wide align-middle duration-500 text-base text-center bg-green-500 text-white rounded-md mt-5"
                  >
                    Update Password
                  </button>
                </form>
              </div>

              {/* Delete Account Section */}
              {/* <div className="p-6 rounded-md shadow dark:shadow-gray-800 bg-white dark:bg-slate-900 mt-6">
                <h5 className="text-lg font-semibold mb-5 text-red-600">
                  Delete Account :
                </h5>

                <p className="text-slate-400 mb-4">
                  Do you want to delete the account? Please press below "Delete
                  Account" button
                </p>

                <Link
                  to=""
                  className="py-2 px-5 inline-block font-semibold tracking-wide align-middle duration-500 text-base text-center bg-red-600 text-white rounded-md"
                >
                  Delete Account
                </Link>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <Switcher />
      <ScrollToTop />
    </>
  );
}